/**
 * Date/time helpers that handle the timezone gotcha we keep hitting:
 * the backend serializes Java {@code LocalDateTime} via Gson as
 * {@code "2026-05-03T12:36:13"} — no Z suffix. JavaScript {@code new
 * Date(string)} treats that as **local time**, but the server runs in UTC,
 * so for any user in a non-UTC zone the parsed Date is off by their UTC
 * offset (e.g. "1 hour ago" appears for a freshly-stored timestamp in BST).
 *
 * Use {@link parseUtcDate} for any timestamp string coming from the
 * backend. It accepts:
 *   - Already-Z-suffixed instants ("…Z" or "…+00:00") — passed through.
 *   - Bare LocalDateTime ("2026-05-03T12:36:13" or "2026-05-03 12:36:13")
 *     — interpreted as UTC and Z-suffixed before parsing.
 *   - Already-Date objects — returned unchanged.
 *   - Numbers — treated as epoch ms (Date(ms) is timezone-neutral).
 *   - null/undefined/empty — null.
 *
 * The dual fix (server emits Z + client parses defensively) is intentional:
 * the server-side fix is the one that's correct, but old endpoints will
 * keep emitting bare LocalDateTime until each one is touched. This helper
 * insulates the FE so the bug can't leak through any code path.
 */
export function parseUtcDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  // Already has a timezone marker → trust it.
  if (/Z$|[+-]\d\d:?\d\d$/.test(trimmed)) {
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
  }

  // Convert "yyyy-MM-dd HH:mm:ss" → "yyyy-MM-ddTHH:mm:ss" (ISO).
  const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
  const d = new Date(iso + 'Z');
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a backend timestamp as a relative phrase ("Just now", "5 minutes ago",
 * "2 hours ago", "3 days ago"). Wraps {@link parseUtcDate} so callers don't
 * need to know about the no-Z gotcha. Returns "Never" for null/empty input.
 */
export function formatRelativeTime(value) {
  const date = parseUtcDate(value);
  if (!date) return 'Never';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffSecs < 0) return 'Just now'; // small clock skew — don't render "in the future"
  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}
